import { Button } from '@design/components/button/Button';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { CSSProperties, ReactNode, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CollapsibleAlfredInput.module.scss';
import { useCollapsibleFooter } from './useCollapsibleFooter';

export interface CollapsibleAlfredInputProps {
  children: ReactNode;
  initiallyFocused?: boolean;
}

export const CollapsibleAlfredInput = ({
  children,
  initiallyFocused = false,
}: CollapsibleAlfredInputProps) => {
  const { t } = useTranslation(['common']);
  const isCollapsible = useFlags(state =>
    state.getFeatureFlagState('REASON-2677-collapsible-alfred-footer')
  );
  const triggerLabel = t('common:sideNavigation.alfred');
  const [pillWidth, setPillWidth] = useState<number>();

  const {
    containerRef,
    isExpanded,
    isSettled,
    containerHandlers,
    triggerHandlers,
  } = useCollapsibleFooter({ isCollapsible, initiallyFocused });

  useLayoutEffect(() => {
    const trigger =
      containerRef.current?.querySelector<HTMLElement>(':scope > button');

    if (trigger) {
      setPillWidth(Math.ceil(trigger.getBoundingClientRect().width));
    }
  }, [containerRef, isCollapsible, triggerLabel]);

  return (
    <div
      className={clsx(styles.collapsible, {
        [styles.expanded]: isExpanded,
        [styles.settled]: isExpanded && isSettled,
      })}
      data-testid="alfred-footer-collapsible"
      ref={containerRef}
      style={
        pillWidth
          ? ({ '--pill-width': `${pillWidth}px` } as CSSProperties)
          : undefined
      }
      {...containerHandlers}
    >
      {isCollapsible && (
        <Button
          className={styles.trigger}
          data-testid="alfred-footer-expand-trigger"
          label={t('common:sideNavigation.alfred')}
          iconName="Alfred"
          variant="empty"
          size="large"
          aria-hidden={isExpanded}
          tabIndex={isExpanded ? -1 : 0}
          {...triggerHandlers}
        />
      )}
      <div className={styles.panel}>
        <div className={styles.panelInner}>{children}</div>
      </div>
    </div>
  );
};
